import * as React from 'react';
import { Form, Button } from 'reactstrap';
import { Checkbox } from 'primereact/checkbox';
import {
  CheckBoxLabel,
  CheckBoxLabelContainer,
} from '../../../components/Checkbox/pickpackCheckbox';
import { FaCopy } from 'react-icons/fa';
import {
  isNullOrWhitespace,
  isValidInvoiceRoutingId,
} from '@/utils/stringHelper';
import { LayoutStoreInstance } from '../../../LayoutStore';
import {
  isValidEmailInput,
  isValidPurchasingOrderEmailInput,
} from '@/utils/email';
import {
  DunningReminderBlockType,
  InvoiceCollectionScope,
} from '../../../graphql/generatedTypes';
import { Alert } from 'reactstrap';
import {
  IsFinance,
  ViewerContext,
} from '../../../providers/ViewerContextProvider';
import { useContext } from 'react';
import { DunningReminderBlockStatus } from '../../../components/DunningReminderBlockStatus';
import i18n from '@/utils/i18n';
import HelpPopover from '../../../components/HelpPopover';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { EditItem, EditSection } from '../../../components/Layout';
import StoreTypeSelector from '../../../views/MerchantComponents/StoreTypeSelector';
import { useQuery } from '@apollo/client';
import { allUserCountriesQuery } from '../../../graphql/queries';
import { InvoiceCollectionDetailFragment } from '../../../graphql/fragments/invoiceCollectionDetail.generated';

type InvoiceCollectionSettingsTabProps = {
  invoiceCollectionId: string;
  invoiceCollection: InvoiceCollectionDetailFragment;
  formData: any;
  setFormData: (data: any) => void;
  handleChange: (e: any) => void;
  handlePaymentTargetDaysChange: (e: any) => void;
  handleAddressChange: (e: any) => void;
  handleSubmit: (e: any) => void;
  updatingInvoiceCollection: boolean;
  disableAutoSend: boolean;
  setDisableAutoSend: (value: boolean) => void;
  collapseInvoiceItems: boolean;
  setCollapseInvoiceItems: (value: boolean) => void;
  needsInvoiceCollectionBreakdown: boolean;
  setNeedsInvoiceCollectionBreakdown: (value: boolean) => void;
  billomatLink: string;
};

export const InvoiceCollectionSettingsTab: React.FC<
  InvoiceCollectionSettingsTabProps
> = ({
  invoiceCollectionId,
  invoiceCollection,
  formData,
  setFormData,
  handleChange,
  handlePaymentTargetDaysChange,
  handleAddressChange,
  handleSubmit,
  updatingInvoiceCollection,
  disableAutoSend,
  setDisableAutoSend,
  collapseInvoiceItems,
  setCollapseInvoiceItems,
  needsInvoiceCollectionBreakdown,
  setNeedsInvoiceCollectionBreakdown,
  billomatLink,
}) => {
  const viewer = useContext(ViewerContext);

  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );
  const countries = countriesData.__type.enumValues.map(
    (country: any) => country.name,
  );

  const invoiceAddressWithTypeName: any =
    invoiceCollection.invoice_address || {};
  const { __typename, ...invoiceAddress } = invoiceAddressWithTypeName;

  return (
    <ContentSection
      title={i18n.t('InvoiceCollection:title')}
      collapsable={false}
      locked
    >
      <div className="input-group mb-3">
        <span className="input-group-text">
          {i18n.t('InvoiceCollection:id')}
        </span>
        <input
          type="text"
          className="form-control"
          value={invoiceCollectionId}
          disabled
        />

        <Button
          onClick={() => {
            navigator.clipboard.writeText(invoiceCollectionId);
            LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
          }}
        >
          <FaCopy />
        </Button>
      </div>
      <Form onSubmit={handleSubmit}>
        <EditSection
          isLoading={updatingInvoiceCollection}
          onSave={() => {
            const syntheticEvent = {
              preventDefault: () => {},
            } as React.FormEvent;
            handleSubmit(syntheticEvent);
          }}
        >
          <EditItem title={i18n.t('Invoices:basicInvoiceInformation')}>
            {/* Name */}
            <div className="input-group mb-3">
              <span className="input-group-text">
                {i18n.t('InvoiceCollection:name')}
              </span>
              <input
                type="text"
                className="form-control"
                name="name"
                onChange={handleChange}
                defaultValue={invoiceCollection.name}
              />
            </div>
            {/* Invoice Routing ID */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:invoiceRoutingId')}
              </span>
              <input
                type="text"
                className={
                  'form-control' +
                  (isValidInvoiceRoutingId(
                    formData.invoice_routing_id ||
                      invoiceCollection.invoice_routing_id,
                  )
                    ? ''
                    : ' is-invalid')
                }
                name="invoice_routing_id"
                onChange={handleChange}
                placeholder={i18n.t('Invoices:routingIdPlaceholder')}
                defaultValue={invoiceCollection.invoice_routing_id || ''}
              />
            </div>
            {/* Invoice Headline */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:additionalInvoiceHeadline')}
              </span>
              <input
                type="text"
                className="form-control"
                name="invoice_headline"
                onChange={handleChange}
                defaultValue={invoiceCollection.invoice_headline || ''}
              />
            </div>
            {/* Order Number */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:orderNumber')}
              </span>
              <input
                type="text"
                className="form-control"
                name="order_number"
                onChange={handleChange}
                defaultValue={invoiceCollection.order_number || ''}
              />
            </div>
            {/* Seller ID */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:sellerId')}
              </span>
              <input
                type="text"
                className="form-control"
                name="seller_id"
                onChange={handleChange}
                defaultValue={invoiceCollection.seller_id || ''}
              />
            </div>
            {/* External Id */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:externalId')}
              </span>
              <input
                type="text"
                className="form-control"
                name="external_id"
                onChange={handleChange}
                defaultValue={invoiceCollection.external_id || ''}
                placeholder={'ES123456'}
              />
            </div>
            {/* Debitor Account Number */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:debitorAccountNumber')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="invoice_collection_debitor_account_number"
                readOnly={true}
                onChange={handleChange}
                defaultValue={
                  invoiceCollection.invoice_collection_debitor_account_number
                }
              />
            </div>
            {/* VAT Number */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:vatNumber')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="vat_number"
                autoComplete="off"
                lang="en-US"
                onChange={(e) => {
                  handleChange(e);
                }}
                defaultValue={invoiceCollection.vat_number || ''}
                placeholder={''}
              />
            </div>
            {/* Billomat Client ID */}
            <div className="input-group mb-3">
              <span className="input-group-text">
                {i18n.t('InvoiceCollection:invoiceBillotmatClientId')}
              </span>
              <input
                type="text"
                className="form-control"
                name="billomat_client_id"
                onChange={handleChange}
                defaultValue={invoiceCollection.billomat_client_id || ''}
              />
            </div>
            {invoiceCollection.billomat_client_id && (
              <div className="mb-3">
                <a
                  href={billomatLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.t('InvoiceCollection:viewInvoicesInBillomat')}
                </a>
              </div>
            )}
            {/* Store Type */}
            <StoreTypeSelector
              storeType={invoiceCollection.type || 'RESTAURANT'}
              onChange={(type: any) => {
                setFormData((prev: any) => ({ ...prev, type }));
              }}
            />
            {/* Disable Automatic Partner Invoice Sending */}
            <div className="input-group mb-3">
              <CheckBoxLabelContainer
                onClick={() => {
                  setFormData({
                    ...formData,
                    disable_automatic_partner_invoice_sending: !disableAutoSend,
                  });
                  setDisableAutoSend(!disableAutoSend);
                }}
              >
                <Checkbox
                  /* @ts-ignore */
                  checked={disableAutoSend}
                />
                <CheckBoxLabel>
                  {i18n.t(
                    'InvoiceCollection:disableAutomaticParnterInvoiceSending',
                  )}
                </CheckBoxLabel>
              </CheckBoxLabelContainer>
            </div>
            {/* Collapse Invoice Items: a penalty bills only collection has one line per store */}
            {invoiceCollection.scope !==
              InvoiceCollectionScope.PenaltyBillsOnly && (
              <div className="input-group mb-3">
                <CheckBoxLabelContainer
                  onClick={() => {
                    setFormData({
                      ...formData,
                      collapse_invoice_items: !collapseInvoiceItems,
                    });
                    setCollapseInvoiceItems(!collapseInvoiceItems);
                  }}
                >
                  <Checkbox
                    /* @ts-ignore */
                    checked={collapseInvoiceItems}
                  />
                  <CheckBoxLabel>
                    {i18n.t('InvoiceCollection:collapseInvoiceItems')}
                  </CheckBoxLabel>
                </CheckBoxLabelContainer>
              </div>
            )}
            {/* Needs Invoice Collection Breakdown */}
            <div className="input-group mb-3">
              <CheckBoxLabelContainer
                onClick={() => {
                  setFormData({
                    ...formData,
                    needs_invoice_collection_breakdown:
                      !needsInvoiceCollectionBreakdown,
                  });
                  setNeedsInvoiceCollectionBreakdown(
                    !needsInvoiceCollectionBreakdown,
                  );
                }}
              >
                <Checkbox
                  /* @ts-ignore */
                  checked={needsInvoiceCollectionBreakdown}
                />
                <CheckBoxLabel>
                  {i18n.t('InvoiceCollection:needsInvoiceCollectionBreakdown')}
                </CheckBoxLabel>
              </CheckBoxLabelContainer>
            </div>
          </EditItem>

          <EditItem title={i18n.t('Billing:invoiceSettings')}>
            {/* Regular Payment Target Days */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:regularPaymentTargetDays')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="regular_payment_target_days"
                autoComplete="off"
                lang="en-US"
                onChange={handlePaymentTargetDaysChange}
                value={
                  formData.regular_payment_target_days !== undefined
                    ? formData.regular_payment_target_days || ''
                    : invoiceCollection.regular_payment_target_days || ''
                }
                placeholder="14"
              />
            </div>
            {/* SEPA 2% Discount */}
            <div className="input-group mb-3">
              <CheckBoxLabelContainer
                onClick={() => {
                  setFormData({
                    ...formData,
                    sepa_2_percent_discount: !(
                      formData.sepa_2_percent_discount ||
                      invoiceCollection.sepa_2_percent_discount
                    ),
                  });
                }}
              >
                <Checkbox
                  /* @ts-ignore */
                  checked={
                    formData.sepa_2_percent_discount !== undefined
                      ? formData.sepa_2_percent_discount
                      : invoiceCollection.sepa_2_percent_discount || false
                  }
                />
                <CheckBoxLabel>
                  {i18n.t('Invoices:sepa2PercentDiscount')}
                </CheckBoxLabel>
              </CheckBoxLabelContainer>
            </div>

            {/* Dunning Reminder Block Status */}
            <DunningReminderBlockStatus
              blockType={invoiceCollection.dunning_reminder_block_type}
              blockUntil={invoiceCollection.dunning_reminder_block_until}
              blockReason={invoiceCollection.dunning_reminder_block_reason}
              translationNamespace="Invoices"
              isBlocked={invoiceCollection.is_dunning_reminder_stopped === true}
            />

            {/* Dunning Reminder Block Change */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:dunningReminderBlockType')}
              </span>
              <select
                className="form-control"
                name="dunning_reminder_block_type"
                value={
                  formData.dunning_reminder_block_type !== undefined
                    ? formData.dunning_reminder_block_type || ''
                    : ''
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dunning_reminder_block_type: e.target.value,
                  })
                }
              >
                <option value="">{i18n.t('Common:selectOption')}</option>
                <option value={DunningReminderBlockType.Permanently}>
                  {i18n.t('Invoices:dunningReminderBlockTypePermanently')}
                </option>
                <option value={DunningReminderBlockType.Temporary}>
                  {i18n.t('Invoices:dunningReminderBlockTypeTemporary')}
                </option>
              </select>
            </div>
            {/* Temporary Info */}
            {(formData.dunning_reminder_block_type !== undefined
              ? formData.dunning_reminder_block_type
              : invoiceCollection.dunning_reminder_block_type) ===
              DunningReminderBlockType.Temporary && (
              <Alert color="info" className="mb-3">
                {i18n.t('Invoices:dunningReminderBlockTemporaryInfo')}
              </Alert>
            )}
            {/* Dunning Reminder Block Reason */}
            {(() => {
              const currentType =
                formData.dunning_reminder_block_type !== undefined
                  ? formData.dunning_reminder_block_type
                  : invoiceCollection.dunning_reminder_block_type;
              return (
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Invoices:dunningReminderBlockReason')}
                  </span>
                  <input
                    type="text"
                    className={
                      'form-control' +
                      (currentType === DunningReminderBlockType.Permanently &&
                      isNullOrWhitespace(
                        formData.dunning_reminder_block_reason ||
                          invoiceCollection.dunning_reminder_block_reason,
                      )
                        ? ' is-invalid'
                        : '')
                    }
                    name="dunning_reminder_block_reason"
                    onChange={handleChange}
                    value={
                      formData.dunning_reminder_block_reason !== undefined
                        ? formData.dunning_reminder_block_reason || ''
                        : invoiceCollection.dunning_reminder_block_reason || ''
                    }
                  />
                </div>
              );
            })()}
            {/* Inkassokennzeichen */}
            <div className="input-group mb-3">
              <CheckBoxLabelContainer
                onClick={() => {
                  if (!IsFinance(viewer)) {
                    return;
                  }
                  setFormData({
                    ...formData,
                    inkassokennzeichen: !(formData.inkassokennzeichen === true),
                  });
                }}
              >
                <Checkbox
                  /* @ts-ignore */
                  checked={
                    formData.inkassokennzeichen != null
                      ? formData.inkassokennzeichen === true
                      : invoiceCollection.inkassokennzeichen === true
                  }
                  disabled={!IsFinance(viewer)}
                />
                <CheckBoxLabel>
                  {i18n.t('Invoices:inkassokennzeichen')}
                </CheckBoxLabel>
              </CheckBoxLabelContainer>
              {!IsFinance(viewer) && (
                <span className="text-muted ms-2">(Finance only)</span>
              )}
            </div>
          </EditItem>

          <EditItem title={i18n.t('Invoices:contactLegalInformation')}>
            {/* Invoice Email */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:invoiceEmail')}
              </span>
              <input
                type="text"
                className={
                  'form-control ' +
                  (!isValidEmailInput(
                    formData.invoice_email ||
                      invoiceCollection.invoice_email ||
                      '',
                  )
                    ? 'is-invalid'
                    : '')
                }
                name="invoice_email"
                autoComplete="off"
                lang="en-US"
                onChange={(e) => {
                  handleChange(e);
                }}
                defaultValue={invoiceCollection.invoice_email || ''}
                placeholder={'bob@example.com;alice@example.com'}
              />
            </div>
            {/* Needs Purchasing Order Number */}
            <div className="input-group mb-1">
              <CheckBoxLabelContainer
                onClick={() => {
                  const needsPurchasingOrderNumber = !(
                    formData.needs_purchasing_order_number ??
                    invoiceCollection.needs_purchasing_order_number
                  );
                  setFormData({
                    ...formData,
                    needs_purchasing_order_number: needsPurchasingOrderNumber,
                    // the contact only exists to be asked for a PO, so it
                    // goes with the switch
                    purchasing_order_email: needsPurchasingOrderNumber
                      ? (formData.purchasing_order_email ??
                        invoiceCollection.purchasing_order_email)
                      : null,
                  });
                }}
              >
                <Checkbox
                  checked={
                    formData.needs_purchasing_order_number !== undefined
                      ? formData.needs_purchasing_order_number
                      : invoiceCollection.needs_purchasing_order_number || false
                  }
                />
                <CheckBoxLabel>
                  {i18n.t('Invoices:needsPurchasingOrderNumber')}
                </CheckBoxLabel>
              </CheckBoxLabelContainer>
              <HelpPopover
                text={i18n.t('Invoices:needsPurchasingOrderNumberHelp')}
                id="needs_purchasing_order_number"
              />
            </div>
            {/* Purchasing Order Email */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:purchasingOrderEmail')}
              </span>
              <input
                type="text"
                className={
                  'form-control ' +
                  (!isValidPurchasingOrderEmailInput(
                    formData.purchasing_order_email ??
                      invoiceCollection.purchasing_order_email ??
                      '',
                  )
                    ? 'is-invalid'
                    : '')
                }
                name="purchasing_order_email"
                autoComplete="off"
                lang="en-US"
                disabled={
                  !(
                    formData.needs_purchasing_order_number ??
                    invoiceCollection.needs_purchasing_order_number
                  )
                }
                onChange={(e) => {
                  handleChange(e);
                }}
                value={
                  formData.purchasing_order_email ??
                  invoiceCollection.purchasing_order_email ??
                  ''
                }
                placeholder={'po1@example.com;po2@example.com'}
              />
              <HelpPopover
                text={i18n.t('Invoices:purchasingOrderEmailHelp')}
                id="purchasing_order_email"
              />
            </div>
            {/* Legal Name */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:legalName')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="legal_name"
                autoComplete="off"
                lang="en-US"
                onChange={(e) => {
                  handleChange(e);
                }}
                defaultValue={invoiceCollection.legal_name || ''}
                placeholder={''}
              />
            </div>
            {/* Invoice Email Subject Prefix */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:emailSubjectPrefixCapitalized')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="invoice_email_subject_prefix"
                autoComplete="off"
                lang="en-US"
                onChange={(e) => {
                  handleChange(e);
                }}
                defaultValue={
                  invoiceCollection.invoice_email_subject_prefix || ''
                }
                placeholder={''}
              />
            </div>
          </EditItem>

          <EditItem title={i18n.t('Invoices:addressInformation')}>
            {/* Street Name */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:street')}
              </span>
              <input
                type="text"
                className="form-control"
                name="streetname"
                autoComplete="off"
                onChange={handleAddressChange}
                value={
                  formData.invoice_address?.streetname !== undefined
                    ? formData.invoice_address.streetname || ''
                    : invoiceAddress.streetname || ''
                }
              />
            </div>
            {/* Street Number */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Invoices:streetNumber')}
              </span>
              <input
                type="text"
                className="form-control"
                onChange={handleAddressChange}
                autoComplete="off"
                name="streetno"
                value={
                  formData.invoice_address?.streetno !== undefined
                    ? formData.invoice_address.streetno || ''
                    : invoiceAddress.streetno || ''
                }
              />
            </div>
            {/* Street Extra */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:streetExtra')}
              </span>
              <input
                type="text"
                className="form-control"
                onChange={handleAddressChange}
                name="street_extra"
                value={
                  formData.invoice_address?.street_extra !== undefined
                    ? formData.invoice_address.street_extra || ''
                    : invoiceAddress.street_extra || ''
                }
              />
            </div>
            {/* ZIP */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:zipCode')}
              </span>
              <input
                type="text"
                className="form-control"
                name="zip"
                onChange={handleAddressChange}
                value={
                  formData.invoice_address?.zip !== undefined
                    ? formData.invoice_address.zip || ''
                    : invoiceAddress.zip || ''
                }
              />
            </div>
            {/* City */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:city')}
              </span>
              <input
                type="text"
                className="form-control"
                name="city"
                autoComplete="off"
                onChange={handleAddressChange}
                value={
                  formData.invoice_address?.city !== undefined
                    ? formData.invoice_address.city || ''
                    : invoiceAddress.city || ''
                }
                placeholder={i18n.t('Invoices:cityPlaceholder')}
              />
            </div>
            {/* Country */}
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:country')}
              </span>
              <select
                className="form-control"
                name="country"
                disabled
                value={invoiceAddress.country}
              >
                {countries.map((country: string) => (
                  <option key={country} value={country}>
                    {i18n.t(`Common:Country:${country}`) as string}
                  </option>
                ))}
              </select>
            </div>
          </EditItem>

          <Button
            variant="primary"
            type="submit"
            disabled={updatingInvoiceCollection}
            className="float-end"
          >
            {!updatingInvoiceCollection
              ? `Update ${invoiceCollection.name}`
              : 'Loading...'}
          </Button>
        </EditSection>
      </Form>
    </ContentSection>
  );
};
